import type { ThemePreference } from '@markbird/shared';
import { render } from '@markbird/markdown';
import { useStore } from '../store/useStore';
import { useDocControllerContext } from '../hooks/DocControllerContext';
import { useExport } from '../hooks/useExport';
import { useShare } from '../hooks/useShare';
import { supportsFileSystemAccess } from './filesystem';
import { t } from '../i18n';
import { askConfirm, askNotice, askPrompt } from './dialogs';
import type { TableCommand } from '../editor/table';
import { parseGoalInput, readWordGoal, withWordGoal } from './wordGoal';
import { openPeriodicNote } from './openPeriodicNote';
import type { VaultPanel } from '../store/useStore';

export interface Command {
  id: string;
  title: string;
  group: 'Document' | 'View' | 'Export' | 'Share' | 'Appearance' | 'Editor' | 'Account';
  /** Human-readable shortcut hint shown in the palette. */
  shortcut?: string;
  keywords?: string;
  run: () => void | Promise<void>;
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
const mod = isMac ? '⌘' : 'Ctrl';

/** The vault panels, each reachable as its own command. */
const VAULT_PANELS: { panel: VaultPanel; title: string; keywords: string }[] = [
  { panel: 'tags', title: 'Vault: tags', keywords: 'tag pane labels filter' },
  {
    panel: 'links',
    title: 'Vault: unresolved links & orphans',
    keywords: 'broken missing backlinks orphan unlinked hygiene',
  },
  { panel: 'graph', title: 'Vault: graph', keywords: 'graph map network connections' },
  {
    panel: 'journal',
    title: 'Vault: journal calendar',
    keywords: 'calendar daily weekly monthly notes diary dates',
  },
];

/** Table helpers; Tab and Shift-Tab cover moving between cells. */
const TABLE_COMMANDS: { command: TableCommand; title: string; keywords: string }[] = [
  { command: 'insert', title: 'Table: insert', keywords: 'new grid create' },
  { command: 'format', title: 'Table: align columns', keywords: 'format tidy pad pretty' },
  { command: 'addRowBelow', title: 'Table: add row below', keywords: 'insert row' },
  { command: 'addColumnRight', title: 'Table: add column right', keywords: 'insert column' },
  { command: 'deleteRow', title: 'Table: delete row', keywords: 'remove row' },
  { command: 'deleteColumn', title: 'Table: delete column', keywords: 'remove column' },
  {
    command: 'cycleAlign',
    title: 'Table: cycle column alignment',
    keywords: 'align left center right justify',
  },
];

const THEME_NEXT: Record<ThemePreference, ThemePreference> = {
  system: 'light',
  light: 'dark',
  dark: 'system',
};

/**
 * The single source of truth for user actions, shared by the command palette
 * and the toolbar. Every primary action is reachable here with a shortcut hint.
 */
export function useCommands(): Command[] {
  const controller = useDocControllerContext();
  const { exportAs } = useExport();
  const { share } = useShare();
  const store = useStore;
  const canFs = supportsFileSystemAccess();

  return [
    {
      id: 'doc.new',
      title: 'New document',
      group: 'Document',
      shortcut: `${mod} N`,
      run: () => controller.create(),
    },
    {
      id: 'doc.today',
      title: "Open today's note",
      group: 'Document',
      keywords: 'daily journal diary today periodic',
      shortcut: `${mod} ⇧ D`,
      run: () => openPeriodicNote('daily', new Date(), controller.open),
    },
    {
      id: 'doc.thisWeek',
      title: "Open this week's note",
      group: 'Document',
      keywords: 'weekly journal periodic review',
      run: () => openPeriodicNote('weekly', new Date(), controller.open),
    },
    {
      id: 'doc.thisMonth',
      title: "Open this month's note",
      group: 'Document',
      keywords: 'monthly journal periodic review',
      run: () => openPeriodicNote('monthly', new Date(), controller.open),
    },
    {
      id: 'doc.duplicate',
      title: 'Duplicate current document',
      group: 'Document',
      run: () => controller.duplicate(store.getState().activeDoc.id),
    },
    {
      id: 'doc.rename',
      title: 'Rename current document…',
      group: 'Document',
      keywords: 'title name',
      run: async () => {
        const { activeDoc } = store.getState();
        if (!activeDoc.id) return;
        const next = await askPrompt({
          title: t('cmd.doc.rename'),
          initial: activeDoc.title,
          confirmLabel: t('sidebar.rename'),
        });
        if (!next) return;
        const { renameDocument } = await import('./library');
        await renameDocument(activeDoc.id, next);
        store.getState().setTitle(next);
      },
    },
    {
      id: 'doc.move',
      title: 'Move to folder…',
      group: 'Document',
      keywords: 'folder organize directory',
      run: () => {
        const id = store.getState().activeDoc.id;
        if (id) store.getState().setMoveToFolderFor(id);
      },
    },
    {
      id: 'doc.history',
      title: 'Version history…',
      group: 'Document',
      keywords: 'snapshot revision restore versions previous',
      run: () => store.getState().setHistoryOpen(true),
    },
    {
      id: 'doc.wordGoal',
      title: 'Set word goal…',
      group: 'Document',
      keywords: 'goal target word count progress writing length',
      run: async () => {
        const current = readWordGoal(store.getState().activeDoc.content);
        const input = await askPrompt({
          title: t('goal.promptTitle'),
          initial: current ? String(current) : '',
          confirmLabel: t('goal.set'),
        });
        if (input === null) return;
        const goal = parseGoalInput(input);
        if (goal === undefined) {
          await askNotice({ title: t('goal.invalidTitle'), body: t('goal.invalidBody') });
          return;
        }
        // Re-read the content: the prompt was open while the document could change.
        const { activeDoc, setContent } = store.getState();
        setContent(withWordGoal(activeDoc.content, goal));
      },
    },
    {
      id: 'doc.tags',
      title: 'Edit tags…',
      group: 'Document',
      keywords: 'tag label folder organize categorize',
      run: () => {
        const id = store.getState().activeDoc.id;
        if (id) store.getState().setTagsEditorFor(id);
      },
    },
    {
      id: 'doc.import',
      title: 'Import Markdown file(s)…',
      group: 'Document',
      keywords: 'upload open file md',
      run: () => store.getState().setImportOpen(true),
    },
    {
      id: 'doc.importUrl',
      title: 'Import from URL…',
      group: 'Document',
      keywords: 'url link web fetch remote import download',
      run: () => store.getState().setImportUrlOpen(true),
    },
    ...(canFs
      ? [
          {
            id: 'doc.openFile',
            title: 'Open file from disk…',
            group: 'Document' as const,
            keywords: 'file system open local edit in place',
            run: async () => {
              const { openMarkdownFromDisk, rememberHandle } = await import('./filesystem');
              const opened = await openMarkdownFromDisk();
              if (!opened) return;
              const { createDocument } = await import('./library');
              const title = opened.name.replace(/\.[^.]+$/, '') || 'Untitled';
              const doc = await createDocument({ title, content: opened.content });
              rememberHandle(doc.id, opened.handle);
              await controller.open(doc.id);
            },
          },
          {
            id: 'doc.saveFile',
            title: 'Save to file (disk)',
            group: 'Document' as const,
            keywords: 'file system save local write',
            run: async () => {
              const { saveMarkdownToDisk } = await import('./filesystem');
              const { activeDoc } = store.getState();
              await saveMarkdownToDisk(activeDoc.id, activeDoc.title, activeDoc.content);
            },
          },
          {
            id: 'doc.saveFileAs',
            title: 'Save a copy to disk…',
            group: 'Document' as const,
            keywords: 'file system save as copy export local',
            run: async () => {
              const { saveMarkdownToDisk } = await import('./filesystem');
              const { activeDoc } = store.getState();
              await saveMarkdownToDisk(activeDoc.id, activeDoc.title, activeDoc.content, true);
            },
          },
        ]
      : []),
    {
      id: 'doc.delete',
      title: 'Delete current document',
      group: 'Document',
      keywords: 'remove trash',
      // No confirmation: deleting is a restorable soft-delete that raises an
      // undo toast, so a blocking prompt would guard a reversible action.
      run: () => {
        const { activeDoc } = store.getState();
        if (activeDoc.id) return controller.remove(activeDoc.id);
      },
    },
    {
      id: 'view.toggleMode',
      title: 'Toggle view mode (split / switch)',
      group: 'View',
      shortcut: `${mod} ⇧ V`,
      run: () => store.getState().toggleViewMode(),
    },
    {
      id: 'view.switchPane',
      title: 'Switch active pane (source / preview)',
      group: 'View',
      shortcut: `${mod} ⇧ P`,
      run: () => store.getState().toggleSwitchPane(),
    },
    {
      id: 'view.toggleSidebar',
      title: 'Toggle sidebar',
      group: 'View',
      shortcut: `${mod} \\`,
      run: () => store.getState().toggleSidebar(),
    },
    {
      id: 'view.toggleOutline',
      title: 'Toggle outline (table of contents)',
      group: 'View',
      keywords: 'toc headings navigate contents',
      run: () => store.getState().toggleOutline(),
    },
    {
      id: 'view.vaultMode',
      title: 'Toggle vault mode (knowledge base)',
      group: 'View',
      keywords: 'wiki knowledge base tags graph backlinks zettelkasten',
      run: () => store.getState().toggleVaultMode(),
    },
    ...VAULT_PANELS.map(({ panel, title, keywords }) => ({
      id: `view.vault.${panel}`,
      title,
      group: 'View' as const,
      keywords,
      run: () => {
        // Asking for a panel is asking for vault mode; making the user turn on
        // a mode first would be a riddle, not a safeguard.
        const state = store.getState();
        if (!state.vaultMode) state.toggleVaultMode();
        store.getState().setVaultPanel(panel);
      },
    })),
    {
      id: 'view.focusMode',
      title: 'Toggle focus mode (zen)',
      group: 'View',
      keywords: 'zen distraction free writing hide chrome',
      run: () => store.getState().toggleFocusMode(),
    },
    {
      id: 'editor.typewriter',
      title: 'Toggle typewriter scrolling',
      group: 'Editor',
      keywords: 'center caret line scroll',
      run: () => store.getState().toggleTypewriter(),
    },
    {
      id: 'app.shortcuts',
      title: 'Keyboard shortcuts',
      group: 'View',
      keywords: 'help keys cheatsheet bindings',
      run: () => store.getState().setShortcutsOpen(true),
    },
    {
      id: 'app.help',
      title: 'Help & support',
      group: 'View',
      keywords: 'help support docs contact feedback faq',
      run: () => window.open('https://markbird.md/support', '_blank', 'noopener,noreferrer'),
    },
    {
      id: 'account.settings',
      title: 'Account & data (export all, delete account)…',
      group: 'Account',
      keywords: 'export backup zip delete account data privacy',
      run: () => store.getState().setAccountDialogOpen(true),
    },
    {
      id: 'editor.find',
      title: 'Find & replace',
      group: 'Editor',
      shortcut: `${mod} F`,
      keywords: 'search replace',
      run: () => store.getState().openSearch?.(),
    },
    ...TABLE_COMMANDS.map(({ command, title, keywords }) => ({
      id: `editor.table.${command}`,
      title,
      group: 'Editor' as const,
      keywords: `table ${keywords}`,
      run: async () => {
        const ran = store.getState().runTable?.(command) ?? false;
        // Every command but "insert" needs a table, and the palette hides where
        // the cursor is — so say why nothing happened instead of doing nothing.
        if (!ran && command !== 'insert') {
          await askNotice({ title: t('table.notInTable'), body: t('table.notInTableBody') });
        }
      },
    })),
    {
      id: 'appearance.customCss',
      title: 'Edit my CSS…',
      group: 'Appearance',
      keywords: 'css theme style snippet custom appearance',
      run: () => store.getState().setSettingsOpen(true),
    },
    {
      id: 'appearance.disableCustomCss',
      // The way out of CSS that hid the way out. Never behind anything styleable.
      title: 'Turn off my CSS',
      group: 'Appearance',
      keywords: 'css disable reset broken unstyle recover',
      run: async () => {
        const { disableAllSnippets } = await import('./customCss');
        const count = await disableAllSnippets();
        await askNotice({
          title: t('css.offTitle'),
          body: count ? t('css.offBody', { n: count }) : t('css.offNone'),
        });
      },
    },
    {
      id: 'app.settings',
      title: 'Settings…',
      group: 'Appearance',
      keywords: 'preferences font line numbers vim',
      run: () => store.getState().setSettingsOpen(true),
    },
    {
      id: 'doc.template',
      title: 'New from template…',
      group: 'Document',
      keywords: 'template new daily meeting blog readme todo',
      run: () => store.getState().setTemplatesOpen(true),
    },
    {
      id: 'doc.trash',
      title: 'Trash (restore deleted)…',
      group: 'Document',
      keywords: 'trash deleted restore recover bin',
      run: () => store.getState().setTrashOpen(true),
    },
    {
      id: 'backup.now',
      title: 'Back up library to folder',
      group: 'Document',
      keywords: 'backup save disk folder local',
      run: async () => {
        const { runFolderBackup } = await import('./backup');
        await runFolderBackup(true);
      },
    },
    {
      id: 'backup.zip',
      title: 'Download library backup (.zip)',
      group: 'Document',
      keywords: 'backup export zip download archive',
      run: async () => {
        const { downloadBackupZip } = await import('./backup');
        await downloadBackupZip();
      },
    },
    {
      id: 'backup.folder',
      title: 'Choose backup folder…',
      group: 'Document',
      keywords: 'backup folder directory location',
      run: async () => {
        const { chooseBackupFolder } = await import('./backup');
        await chooseBackupFolder();
      },
    },
    {
      id: 'site.publish',
      title: 'Publish a folder as a site…',
      group: 'Document',
      keywords: 'publish site website pages folder wiki handbook share',
      run: () => {
        store.getState().setPublishSiteOpen(true);
      },
    },
    {
      id: 'folderSync.now',
      title: 'Sync files now',
      group: 'Document',
      keywords: 'folder sync disk files directory obsidian vault github repository repo',
      run: async () => {
        const { getFolderSyncState, runFolderSync } = await import('./folderSync');
        // Not set up yet: the settings are where that happens.
        if (!(await getFolderSyncState()).enabled) store.getState().setSettingsOpen(true);
        else await runFolderSync(true);
      },
    },
    {
      id: 'doc.emptyTrash',
      title: 'Empty trash (permanent)',
      group: 'Document',
      keywords: 'delete purge',
      run: async () => {
        const ok = await askConfirm({
          title: t('cmd.doc.emptyTrash'),
          body: t('cmd.confirm.emptyTrash'),
          confirmLabel: t('trash.emptyAll'),
          danger: true,
        });
        if (ok) {
          const { emptyTrash } = await import('./library');
          await emptyTrash();
        }
      },
    },
    {
      id: 'export.md',
      title: 'Export as Markdown (.md)',
      group: 'Export',
      keywords: 'download save source',
      run: () => exportAs('md'),
    },
    {
      id: 'export.html',
      title: 'Export as HTML',
      group: 'Export',
      keywords: 'download save',
      run: () => exportAs('html'),
    },
    {
      id: 'export.pdf',
      title: 'Export as PDF',
      group: 'Export',
      keywords: 'print download',
      run: () => exportAs('pdf'),
    },
    {
      id: 'export.docx',
      title: 'Export as DOCX (Word)',
      group: 'Export',
      keywords: 'word download',
      run: () => exportAs('docx'),
    },
    {
      id: 'export.slides',
      title: 'Export as slides (HTML)',
      group: 'Export',
      keywords: 'presentation deck slideshow reveal present download',
      run: () => exportAs('slides'),
    },
    {
      id: 'export.copyHtml',
      title: 'Copy rendered HTML',
      group: 'Export',
      keywords: 'clipboard',
      run: () => navigator.clipboard?.writeText(render(store.getState().activeDoc.content)),
    },
    {
      id: 'export.copyMarkdown',
      title: 'Copy Markdown',
      group: 'Export',
      keywords: 'clipboard source',
      run: () => navigator.clipboard?.writeText(store.getState().activeDoc.content),
    },
    {
      id: 'share.create',
      title: 'Share read-only link',
      group: 'Share',
      keywords: 'link publish',
      run: () => share('read'),
    },
    {
      id: 'share.options',
      title: 'Share with options (access + expiry)…',
      group: 'Share',
      keywords: 'link publish expire expiry permission editable',
      run: () => store.getState().setShareOptionsOpen(true),
    },
    // Managing links needs no account: links made on this device are listed
    // with the secret that deletes them, which is the only way to take back a
    // link made without one.
    {
      id: 'share.manage',
      title: 'Manage shared links…',
      group: 'Share',
      keywords: 'revoke list copy shares delete',
      run: () => store.getState().setSharesOpen(true),
    },
    ...(store.getState().user
      ? [
          {
            id: 'share.createEdit',
            title: 'Share editable link',
            group: 'Share' as const,
            keywords: 'link publish collaborate edit',
            run: () => share('edit'),
          },
        ]
      : []),
    {
      id: 'appearance.cycleTheme',
      title: 'Cycle theme (auto / light / dark)',
      group: 'Appearance',
      run: () => {
        const t = store.getState().theme;
        store.getState().setTheme(THEME_NEXT[t]);
      },
    },
    {
      id: 'appearance.light',
      title: 'Theme: Light',
      group: 'Appearance',
      run: () => store.getState().setTheme('light'),
    },
    {
      id: 'appearance.dark',
      title: 'Theme: Dark',
      group: 'Appearance',
      run: () => store.getState().setTheme('dark'),
    },
    {
      id: 'appearance.system',
      title: 'Theme: System',
      group: 'Appearance',
      run: () => store.getState().setTheme('system'),
    },
    {
      id: 'editor.toggleVim',
      title: 'Toggle Vim mode',
      group: 'Editor',
      keywords: 'vi keybindings',
      run: () => store.getState().toggleVim(),
    },
    store.getState().user
      ? {
          id: 'account.signOut',
          title: 'Sign out',
          group: 'Account',
          keywords: 'account logout',
          run: async () => {
            const { signOutUser } = await import('./auth');
            await signOutUser();
          },
        }
      : {
          id: 'account.signIn',
          title: 'Sign in to sync…',
          group: 'Account',
          keywords: 'account login google github email',
          run: () => store.getState().setAuthOpen(true),
        },
  ];
}
